import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import ProfileCombo from '../../sharedComponents/ProfileCombo';
import useGetCustomerProfile from '../../hooks/useGetCustomerProfile';
import CustomerCompanyInfoService from './CustomerCompanyInfoService';

const isValidTaxNo13 = (value) => /^\d{13}$/.test(String(value || ''));

const CustomerCompanyInfoPage = () => {
  const { profiles, getCustomerProfiles } = useGetCustomerProfile();
  const [selectedSmileName, setSelectedSmileName] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedRowCustomerId, setSelectedRowCustomerId] = useState(null);

  const [form, setForm] = useState({
    taxNo: '',
    companyName: '',
    companyAddress: '',
  });

  const selectedProfile = useMemo(() => {
    if (!selectedSmileName) return null;
    return (profiles || []).find((p) => p.smileName === selectedSmileName) || null;
  }, [profiles, selectedSmileName]);

  const refreshList = async () => {
    setLoading(true);
    try {
      const data = await CustomerCompanyInfoService.listAll();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to load records';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCustomerProfiles();
    refreshList();
  }, []);

  useEffect(() => {
    if (!selectedProfile) {
      setSelectedCustomerId(null);
      return;
    }
    setSelectedCustomerId(selectedProfile.id);
  }, [selectedProfile]);

  const loadExisting = async (customerId) => {
    setLoading(true);
    try {
      const data = await CustomerCompanyInfoService.getByCustomerId(customerId);
      if (!data) {
        setForm({ taxNo: '', companyName: '', companyAddress: '' });
        return;
      }
      setForm({
        taxNo: data.taxNo || '',
        companyName: data.companyName || '',
        companyAddress: data.companyAddress || '',
      });
      setSelectedRowCustomerId(customerId);
      const p = data.CustomerProfile;
      if (p?.smileName) setSelectedSmileName(p.smileName);
      if (p?.id) setSelectedCustomerId(p.id);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to load company info';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const startCreateNew = () => {
    setSelectedRowCustomerId(null);
    setSelectedSmileName('');
    setSelectedCustomerId(null);
    setForm({ taxNo: '', companyName: '', companyAddress: '' });
  };

  const onChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!selectedCustomerId) {
      toast.error('Please select a customer first');
      return;
    }
    if (!isValidTaxNo13(form.taxNo)) {
      toast.error('taxNo ต้องเป็นตัวเลข 13 หลัก');
      return;
    }
    if (!form.companyName.trim() || !form.companyAddress.trim()) {
      toast.error('กรุณากรอก Company Name และ Address');
      return;
    }

    setLoading(true);
    try {
      await CustomerCompanyInfoService.upsert({
        customerId: selectedCustomerId,
        taxNo: String(form.taxNo),
        companyName: form.companyName,
        companyAddress: form.companyAddress,
      });
      toast.success('Saved customer company info');
      await refreshList();
      await loadExisting(selectedCustomerId);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Save failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRowCustomerId) return;
    setLoading(true);
    try {
      await CustomerCompanyInfoService.remove(selectedRowCustomerId);
      toast.success('Deleted customer company info');
      await refreshList();
      startCreateNew();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Delete failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-6 py-4">
      <h2 className="text-4xl font-bold mb-4">Customer Company Info (Tax Invoice)</h2>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-5 bg-white shadow-lg rounded-md p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-lg font-bold">Records</div>
            <button
              onClick={refreshList}
              disabled={loading}
              className="px-3 py-1 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50"
            >
              Refresh
            </button>
          </div>

          <div className="overflow-y-auto border rounded-md" style={{ maxHeight: '70vh' }}>
            {rows.length === 0 ? (
              <div className="p-4 text-sm text-gray-600">No records</div>
            ) : (
              rows.map((r) => {
                const p = r.CustomerProfile;
                const isActive = String(r.customerId) === String(selectedRowCustomerId);
                return (
                  <button
                    key={r.customerId}
                    onClick={() => loadExisting(r.customerId)}
                    className={`w-full text-left px-3 py-2 border-b hover:bg-gray-50 ${isActive ? 'bg-indigo-50' : ''}`}
                  >
                    <div className="font-semibold">{p?.contact || '-'} ({p?.smileName || '-'})</div>
                    <div className="text-xs text-gray-600">
                      taxNo: {r.taxNo} | {r.companyName}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="col-span-7 bg-white shadow-lg rounded-md p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-lg font-bold">{selectedRowCustomerId ? 'Edit' : 'Create'}</div>
            <button
              onClick={startCreateNew}
              disabled={loading}
              className="px-3 py-1 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50"
            >
              New
            </button>
          </div>

          <div className="mb-4">
            <div className="text-sm text-gray-700 mb-2">Select customer</div>
            <ProfileCombo
              profiles={profiles || []}
              onChange={(smileName) => setSelectedSmileName(smileName)}
              initialSmileName={selectedSmileName}
            />
            {selectedProfile && (
              <div className="mt-2 text-sm text-gray-700">
                Selected: <b>{selectedProfile.contact}</b> ({selectedProfile.smileName}) — id: {selectedProfile.id}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900">Tax No (13 digits)</label>
              <input
                className="mt-1 w-full rounded-md border py-2 px-3"
                value={form.taxNo}
                onChange={(e) => onChange('taxNo', e.target.value.replace(/\D/g, '').slice(0, 13))}
                disabled={loading}
                placeholder="0123456789012"
              />
              <div className={`mt-1 text-xs ${form.taxNo && !isValidTaxNo13(form.taxNo) ? 'text-red-600' : 'text-gray-500'}`}>
                {form.taxNo && !isValidTaxNo13(form.taxNo) ? 'Invalid taxNo (must be 13 digits)' : ' '}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900">Company Name</label>
              <input
                className="mt-1 w-full rounded-md border py-2 px-3"
                value={form.companyName}
                onChange={(e) => onChange('companyName', e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-900">Company Address</label>
              <textarea
                className="mt-1 w-full rounded-md border py-2 px-3"
                rows={6}
                value={form.companyAddress}
                onChange={(e) => onChange('companyAddress', e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              onClick={handleSave}
              disabled={loading || !selectedCustomerId}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleDelete}
              disabled={loading || !selectedRowCustomerId}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerCompanyInfoPage;

